/**
 * KAN-647: таблица A/B вариантов первого сообщения Telegram-аутрича.
 *
 * Строка = вариант шаблона: отправлено, ответили, доля ответа с 95% интервалом
 * Уилсона, интерес и отказ по воронке, пометка "мало данных". Шаблоны редактируются
 * в настройках канала Telegram (поле "Шаблоны первого сообщения").
 *
 * KAN-651: строки вариантов-брифов показываются как "Вариант A: <бриф>".
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGreetingAb } from '@/hooks/useGreetingAb';
import type { GreetingVariantStats } from '@/services/api/greetingAb';

const STATUS_NOTE: Record<GreetingVariantStats['status'], string> = {
  current: '',
  removed: 'вариант удалён из шаблонов',
  unknown: 'вариант не определён',
};

function pct(value: number | null): string {
  return value === null ? '-' : `${Math.round(value * 100)}%`;
}

function variantTitle(row: GreetingVariantStats): string {
  if (row.kind === 'brief') {
    const head = row.label ? `Вариант ${row.label}` : 'Удалённый вариант';
    return row.preview ? `${head}: ${row.preview}` : head;
  }
  return row.preview || 'Не определён';
}

function formatRate(row: GreetingVariantStats): string {
  if (row.reply_rate === null) return '-';
  return `${pct(row.reply_rate)} (${pct(row.ci_low)}..${pct(row.ci_high)})`;
}

interface GreetingAbTableProps {
  /** KAN-752: статистика рассылки одного оператора; без него - вся организация. */
  operatorId?: string;
}

export function GreetingAbTable({ operatorId }: GreetingAbTableProps = {}): JSX.Element | null {
  const { data } = useGreetingAb(operatorId);
  const variants = data?.variants ?? [];
  if (variants.length === 0) return null;

  return (
    <section data-testid="greeting-ab" className="mt-6">
      <h4 className="text-sm font-semibold text-gray-900">Статистика вариантов</h4>
      <p className="mb-2 text-xs text-gray-500">
        Ответили: клиент написал после первого сообщения. В скобках 95% доверительный
        интервал. Меньше {data?.low_data_threshold ?? 30} отправленных: выводы делать рано.
        Варианты с подстановкой, которой нет у записи (например, компания), этой записи не
        достаются.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Вариант</TableHead>
              <TableHead className="text-right">Отправлено</TableHead>
              <TableHead className="text-right">Ответили</TableHead>
              <TableHead className="text-right">% ответа</TableHead>
              <TableHead className="text-right">Интерес</TableHead>
              <TableHead className="text-right">Отказ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {variants.map((row) => (
              <TableRow key={row.variant_id} data-testid={`greeting-ab-row-${row.variant_id}`}>
                <TableCell className="max-w-md text-sm text-gray-800">
                  <span title={row.text}>{variantTitle(row)}</span>
                  {STATUS_NOTE[row.status] && (
                    <span className="ml-2 text-xs text-gray-500">{STATUS_NOTE[row.status]}</span>
                  )}
                  {row.low_data && (
                    <span
                      data-testid={`greeting-ab-low-data-${row.variant_id}`}
                      className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                    >
                      мало данных
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right text-sm">{row.sent}</TableCell>
                <TableCell className="text-right text-sm">{row.replied}</TableCell>
                <TableCell className="text-right text-sm whitespace-nowrap">
                  {formatRate(row)}
                </TableCell>
                <TableCell className="text-right text-sm">{row.interested}</TableCell>
                <TableCell className="text-right text-sm">{row.lost}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

export default GreetingAbTable;
